import type { ReactNode } from 'react'
import { Link } from 'react-router'
import escapeRegExp from 'lodash/escapeRegExp'
import { ROUTE_DOCUMENT_REVIEW, ROUTE_ORDER_EVENTS, ROUTE_ORDERS, ROUTE_PAYMENT } from '@klear/constants'
import { recordLabel } from '@/app/document-review/review-labels'

const LINK_CLASS = 'text-foreground hover:text-foreground underline font-mono font-medium'

function linkProps(whileDeciding: boolean) {
  return whileDeciding ? { tabIndex: -1, target: '_blank', rel: 'noopener noreferrer' } : {}
}

type RecordLinkProps = {
  recordIdDisplay: string | null
  orderEventId: number
  deleted?: boolean
  whileDeciding?: boolean
}

export function RecordLink({ recordIdDisplay, orderEventId, deleted = false, whileDeciding = false }: RecordLinkProps) {
  if (deleted) {
    return (
      <span className="inline-flex items-baseline gap-1.5">
        <span className="text-muted-foreground font-mono font-medium line-through">
          {recordLabel(recordIdDisplay, orderEventId)}
        </span>
        <span className="text-xs text-red-700 dark:text-red-400">deleted</span>
      </span>
    )
  }
  if (recordIdDisplay === null) {
    return <span className="font-mono font-medium">{recordLabel(null, orderEventId)}</span>
  }
  return <RecordDisplayLink recordIdDisplay={recordIdDisplay} whileDeciding={whileDeciding} />
}

export function RecordDisplayLink({
  recordIdDisplay,
  whileDeciding = false
}: {
  recordIdDisplay: string
  whileDeciding?: boolean
}) {
  return (
    <Link to={`/${ROUTE_ORDER_EVENTS}/${recordIdDisplay}`} className={LINK_CLASS} {...linkProps(whileDeciding)}>
      {recordIdDisplay}
    </Link>
  )
}

export function PaymentLink({
  recordIdDisplay,
  paymentId,
  deleted = false
}: {
  recordIdDisplay: string | null
  paymentId: number
  deleted?: boolean
}) {
  const label = recordLabel(recordIdDisplay, paymentId)
  if (deleted) {
    return (
      <span className="inline-flex items-baseline gap-1.5">
        <span className="text-muted-foreground font-mono font-medium line-through">{label}</span>
        <span className="text-xs text-red-700 dark:text-red-400">deleted</span>
      </span>
    )
  }
  return (
    <Link to={`/${ROUTE_PAYMENT}/${paymentId}`} className={LINK_CLASS}>
      {label}
    </Link>
  )
}

export function ReviewLink({ processingId, whileDeciding = false }: { processingId: string; whileDeciding?: boolean }) {
  return (
    <Link to={`/${ROUTE_DOCUMENT_REVIEW}/${processingId}`} className={LINK_CLASS} {...linkProps(whileDeciding)}>
      review {processingId}
    </Link>
  )
}

export function OrderLink({
  orderIdDisplay,
  whileDeciding = false
}: {
  orderIdDisplay: string
  whileDeciding?: boolean
}) {
  return (
    <Link to={`/${ROUTE_ORDERS}/${orderIdDisplay}`} className={LINK_CLASS} {...linkProps(whileDeciding)}>
      {orderIdDisplay}
    </Link>
  )
}

export function TextWithLink({ text, linked, link }: { text: string; linked: string; link: ReactNode }) {
  const match = new RegExp(`(?<![\\w-])${escapeRegExp(linked)}(?![\\w-])`).exec(text)
  if (match === null) return text
  return (
    <>
      {text.slice(0, match.index)}
      {link}
      {text.slice(match.index + linked.length)}
    </>
  )
}
